import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import i18next from 'i18next'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'


const StyledMenuButton = styled.button<{ isOpen?: boolean }>`
  position: relative;
  width: 100%;
  min-width: 140px;
  height: 100%;
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  height: 35px;
  background-color: ${({ theme, isOpen }) => (isOpen ? 'rgba(44, 47, 54, 0.95)' : theme.bg3)};

  padding: 0.4rem 1rem;
  border-radius: ${({ isOpen }) => (isOpen ? '16px 16px 0 0' : '0.5rem')};
  border: ${({ isOpen }) => (isOpen ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')};
  border-bottom: ${({ isOpen }) => (isOpen ? 'none' : 'none')};
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  :hover,
  :focus {
    cursor: pointer;
    outline: none;
    background-color: ${({ theme, isOpen }) => (isOpen ? 'rgba(44, 47, 54, 0.95)' : theme.bg4)};
  }

  svg {
    margin-top: 2px;
  }
`

const LanguageText = styled.span`
  color: ${({ theme }) => theme.text1};
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
`

const StyledMenu = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  text-align: left;

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    margin-left: 0;
    margin-right: 0.3rem;
  `};
`

const MenuFlyout = styled.span`
  width: 100%;
  background-color: rgba(44, 47, 54, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.3);

  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: none;

  border-radius: 0 0 16px 16px;
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  overflow: hidden;
  padding: 0.5rem 0 0.75rem 0;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const MenuItem = styled.div`
  padding: 0.85rem 1.5rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  transition: all 0.15s ease-in-out;
  font-size: 0.95rem;
  font-weight: 400;
  
  :hover {
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  > * {
    stroke: ${({ theme }) => theme.text2};
  }
`

const MenuItemActive = styled(MenuItem)`
  color: rgba(255, 255, 255, 1);
  font-weight: 500;
  background-color: transparent;
  
  :hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  > * {
    stroke: ${({ theme }) => theme.text1};
  }
`

const SUPPORTED_LANGUAGES = [
  { code: 'zh-CN', name: '中文简体' },
  { code: 'zh-TW', name: '中文繁體' },
  { code: 'en', name: 'English' }
]

export default function LanguageSelector() {
  const node = useRef<HTMLDivElement>()
  const [open, setOpen] = useState(false)

  useOnClickOutside(node, open ? () => setOpen(false) : undefined)

  const handleLanguageChange = (languageCode: string) => {
    i18next.changeLanguage(languageCode)
    setOpen(false)
  }

  const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === i18next.language)

  return (
    <StyledMenu ref={node as any}>
      <StyledMenuButton isOpen={open} onClick={() => setOpen(!open)}>
        <LanguageText>{currentLanguage?.name || '中文简体'}</LanguageText>
      </StyledMenuButton>
      {open && (
        <MenuFlyout>
          {SUPPORTED_LANGUAGES.map(language => {
            const isActive = language.code === i18next.language
            const MenuItemComponent = isActive ? MenuItemActive : MenuItem

            return (
              <MenuItemComponent key={language.code} onClick={() => handleLanguageChange(language.code)}>
                {language.name}
              </MenuItemComponent>
            )
          })}
        </MenuFlyout>
      )}
    </StyledMenu>
  )
}
